import { Component, EventEmitter, Output, SecurityContext } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { StudiesFilter, FilterCriteria, SortCriteria } from '@models/studiesFilter.model';
import { NgForm, NgModel, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { LoggingService } from '@services/logging.service';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-studies-search-filter',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: StudiesSearchFilterComponent,
      multi: true,
    },
  ],
  templateUrl: './studies-search-filter.component.html',
  styleUrls: ['./studies-search-filter.component.css'],
})
export class StudiesSearchFilterComponent implements ControlValueAccessor {
  searchText: string;
  selectedSort: string;
  studiesFilter: StudiesFilter;
  sortOptions: SelectItem[];
  filterEntityOptions: SelectItem[];
  filterOperationOptions: SelectItem[];
  filterCriterias: FilterCriteria[];
  expanded = false;
  private onChange: (arg0: any) => void;
  private onTouch: (arg0: any) => void;
  @Output() applyFilter: EventEmitter<any> = new EventEmitter();

  applyClicked = false;
  enterPressed = false;

  constructor(public _DomSanitizationService: DomSanitizer, private logger: LoggingService) {
    this.studiesFilter = new StudiesFilter();
    this.sortOptions = this.getSortOptions();
    this.filterEntityOptions = this.getFilterEntityOptions();
    this.filterOperationOptions = this.getFilterOperationOptions();
    this.selectedSort = '-1';
    this.resetFilters();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(filter: StudiesFilter): void {
    this.studiesFilter = filter || new StudiesFilter();
  }

  onSearchClick() {
    if (this.searchText !== undefined && this.searchText !== '') {
      const searchFilter = new FilterCriteria();
      searchFilter.entity = '-1';
      searchFilter.opcode = '0';
      this.searchText = this._DomSanitizationService.sanitize(SecurityContext.HTML, this.searchText);
      searchFilter.value = this.searchText;

      this.studiesFilter.filterCriteria = [searchFilter];
    } else {
      this.studiesFilter.filterCriteria = null;
    }
    this.logger.info(this.constructor.name, 'onSearchClick', `[${JSON.stringify(this.studiesFilter)}]`);
    this.applyFilter.emit(this.studiesFilter);
  }

  onSortChange() {
    const sortCriteria = new SortCriteria();
    if (this.selectedSort === '2a') {
      // Client Name descending
      sortCriteria.entity = '2';
      sortCriteria.isAscending = false;
    } else {
      sortCriteria.entity = this.selectedSort;
      sortCriteria.isAscending = true;
    }
    this.studiesFilter.sortCriteria = sortCriteria;
    this.logger.info(this.constructor.name, 'onSortChange', `[${JSON.stringify(this.studiesFilter)}]`);
    this.applyFilter.emit(this.studiesFilter);
  }

  onClickExpand() {
    this.expanded = !this.expanded;
  }

  resetFilters() {
    this.filterCriterias = [];
    this.filterCriterias.push(new FilterCriteria());
  }

  onResetFilterClick() {
    this.applyClicked = false;
    this.resetFilters();
    this.searchText = undefined;
    this.studiesFilter.filterCriteria = null;
    this.applyFilter.emit(this.studiesFilter);
    this.expanded = false;
  }

  onFormSubmit(form: NgForm) {
    this.applyClicked = true && !this.enterPressed;

    if (form.valid) {
      this.searchText = undefined;

      this.studiesFilter.filterCriteria = this.filterCriterias;
      this.logger.info(this.constructor.name, 'applyFilterClick', `[${JSON.stringify(this.studiesFilter)}]`);
      this.applyFilter.emit(this.studiesFilter);
    }

    this.enterPressed = false;
  }

  onAddFilterClick() {
    this.filterCriterias.push(new FilterCriteria());
  }

  onRemoveFilterClick(filterCriteria: FilterCriteria) {
    const index = this.filterCriterias.indexOf(filterCriteria);
    if (index > -1) {
      this.filterCriterias.splice(index, 1);
    }
  }

  onEnterKey(): void {
    this.enterPressed = true;
    this.onSearchClick();
  }

  showValidationMessage(item: NgModel): boolean {
    return item.errors && (this.applyClicked || !item.pristine || item.touched);
  }

  getUniqueName(name: string, filter: FilterCriteria): string {
    const index = this.filterCriterias.indexOf(filter);
    return name + index;
  }

  getSortOptions(): SelectItem[] {
    return [
      { label: 'Most Recent First', value: '-1' },
      { label: 'Study ID', value: '0' },
      { label: 'Study Name', value: '1' },
      { label: 'Client Name (A-Z)', value: '2' },
      { label: 'Client Name (Z-A)', value: '2a' },
      { label: 'Office Location', value: '3' },
      { label: 'Region/Country', value: '4' },
      { label: 'Function', value: '5' },
      { label: 'Industry/Sectors', value: '6' },
      { label: 'Business Description', value: '7' },
      { label: 'Created Date', value: '8' },
      { label: 'Status', value: '9' },
      { label: 'Period of Study', value: '10' },
      { label: 'Fiscal Year', value: '11' },
    ];
  }

  getFilterEntityOptions(): SelectItem[] {
    return [
      { label: 'Study ID', value: 0 },
      { label: 'Study Name', value: 1 },
      { label: 'Client Name', value: 2 },
      { label: 'Office Location', value: 3 },
      { label: 'Region/Country', value: 4 },
      { label: 'Function', value: 5 },
      { label: 'Industry/Sectors', value: 6 },
      { label: 'Business Description', value: 7 },
      { label: 'Created Date', value: 8 },
      { label: 'Status', value: 9 },
      { label: 'Period of Study', value: 10 },
      { label: 'Fiscal Year', value: 11 },
    ];
  }

  getFilterOperationOptions(): SelectItem[] {
    return [
      { label: 'Contains', value: 0 },
      { label: 'Begins With', value: 1 },
      { label: 'Ends With', value: 2 },
      { label: 'Is equal to', value: 3 },
      { label: 'Is not equal to', value: 4 },
    ];
  }
}
